import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import type { Locale, TranslationKey } from '../i18n.js';
import type { SessionResponse } from '../lib/api.js';
import type { ThemeMode } from '../lib/theme.js';

type T = (key: TranslationKey, vars?: Record<string, string | number>) => string;

/**
 * Le menu de la personne connectée — même découpe que celui d'ODE.
 *
 * Un bouton rond, un panneau, et la **déconnexion isolée en bas, en rouge** : c'est une
 * action qu'on ne déclenche pas par erreur en visant la langue.
 *
 * Ce qui change par rapport à ODE : le **badge**. Dans Mnemo, ce qu'une personne voit
 * dépend entièrement de son périmètre — et un périmètre vide ne produit aucune erreur,
 * juste un tableau vide. Le badge le dit donc à l'écran, en permanence.
 */

type Props = {
  t: T;
  session: SessionResponse;
  locale: Locale;
  mode: ThemeMode;
  onLocale: (locale: Locale) => void;
  onMode: (mode: ThemeMode) => void;
  onPersona: (persona: string) => void;
  onHelp: () => void;
  onLogout: () => void;
};

/** Petit contrôle segmenté, comme dans ODE : deux ou trois choix exclusifs, côte à côte. */
function Segmented<V extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: V;
  options: { value: V; label: string }[];
  onChange: (value: V) => void;
  label: string;
}) {
  return (
    <div
      className="flex items-center gap-0.5 rounded-lg bg-bg p-0.5"
      role="group"
      aria-label={label}
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
          className={`rounded-md px-2 py-1 text-xs font-medium transition-colors ${
            value === o.value ? 'bg-surface text-fg shadow-sm' : 'text-muted hover:text-fg'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Le badge de périmètre.
 *
 * ⚠️ Trois états, et le troisième est le plus important : sans établissement, la personne
 * ne voit **rien**, et rien à l'écran ne le lui dirait. Un tableau vide ressemble à une
 * base vide. Le badge est ce qui distingue « il n'y a rien » de « vous n'avez droit à rien ».
 */
export function ScopeBadge({ t, session }: { t: T; session: SessionResponse }) {
  const user = session.user;
  if (!user) return null;

  if (user.isAdmin) {
    return (
      <span className="chip bg-accent text-accent-fg" title={t('badge.adminHint')}>
        {t('badge.admin')}
      </span>
    );
  }
  if (!user.establishments || user.establishments.length === 0) {
    return (
      <span className="chip bg-warn/20 text-fg" title={t('badge.noScopeHint')}>
        <span aria-hidden="true">⚠ </span>
        {t('badge.noScope')}
      </span>
    );
  }
  return (
    <span className="chip bg-accent/15 text-fg" title={t('badge.scopeHint')}>
      {user.establishments.join(' · ')}
    </span>
  );
}

export function UserMenu({
  t,
  session,
  locale,
  mode,
  onLocale,
  onMode,
  onPersona,
  onHelp,
  onLogout,
}: Props) {
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);

  // Échap ferme, et un clic ailleurs aussi (Charte §6). Sans ces deux-là, un menu ouvert
  // sur mobile piège la personne : rien à l'écran ne suggère comment le refermer.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      if (!container.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  const user = session.user;

  return (
    <div className="relative" ref={container}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={t('menu.open')}
        title={t('menu.open')}
        className={`flex h-11 w-11 items-center justify-center rounded-full border transition-colors ${
          open
            ? 'border-accent bg-bg text-fg'
            : 'border-border text-muted hover:bg-bg hover:text-fg'
        }`}
      >
        {/* Une vraie icône plutôt que le caractère « ⚙ » : celui-ci se rend en petit et
            différemment d'une plateforme à l'autre, et se retrouvait perdu dans son bouton. */}
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-5 w-5"
        >
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full z-20 mt-2 w-72 origin-top-right rounded-xl border border-card-border bg-surface p-1.5 shadow-lg">
          {/* L'identité, répétée ici : sur mobile, elle n'est pas dans la barre. */}
          {user && (
            <div className="mb-1 border-b border-border px-2.5 py-2">
              <p className="truncate text-sm font-medium">{user.name}</p>
              <p className="truncate text-xs text-muted">{user.email}</p>
              <div className="mt-1.5">
                <ScopeBadge t={t} session={session} />
              </div>
            </div>
          )}

          {/* Mode factice : le choix d'identité vit ici, pas dans la barre. */}
          {session.stubAuth && (
            <div className="px-2.5 py-1.5">
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-muted">{t('persona.label')}</span>
                <select
                  className="field py-1 text-sm"
                  value={user?.id ?? ''}
                  onChange={(e) => {
                    onPersona(e.target.value);
                    setOpen(false);
                  }}
                >
                  {session.personas.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}

          <div className="flex items-center justify-between gap-2 px-2.5 py-1.5">
            <span className="text-sm text-muted">{t('menu.language')}</span>
            <Segmented
              label={t('menu.language')}
              value={locale}
              onChange={onLocale}
              options={[
                { value: 'fr' as Locale, label: 'FR' },
                { value: 'de' as Locale, label: 'DE' },
              ]}
            />
          </div>

          <div className="flex items-center justify-between gap-2 px-2.5 py-1.5">
            <span className="text-sm text-muted">{t('menu.theme')}</span>
            <Segmented
              label={t('menu.theme')}
              value={mode}
              onChange={onMode}
              options={[
                { value: 'system' as ThemeMode, label: t('theme.system') },
                { value: 'light' as ThemeMode, label: t('theme.light') },
                { value: 'dark' as ThemeMode, label: t('theme.dark') },
              ]}
            />
          </div>

          <div className="mx-1 my-1.5 h-px bg-border" />

          {/* L'aide est au même endroit que les réglages — sous la roue dentée, là où on
              cherche « comment ça marche ». Elle décrit l'existant, pas l'intention. */}
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onHelp();
            }}
            className="flex min-h-[44px] w-full items-center gap-2.5 rounded-lg px-2.5 text-sm transition-colors hover:bg-bg"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-[18px] w-[18px]"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            {t('menu.help')}
          </button>

          {/* L'administration, rangée avec les réglages plutôt qu'en onglet (25.09.2026) :
              elle ne concerne qu'une poignée de personnes. Masquée aux autres par confort —
              c'est le serveur qui protège, en répondant 404 (ADM-01). */}
          {user?.isAdmin && (
            <Link
              to="/admin"
              onClick={() => setOpen(false)}
              className="flex min-h-[44px] w-full items-center gap-2.5 rounded-lg px-2.5 text-sm transition-colors hover:bg-bg"
            >
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-[18px] w-[18px]"
              >
                <path d="M12 3 4 6v6c0 4.5 3.4 8.3 8 9 4.6-.7 8-4.5 8-9V6l-8-3z" />
                <path d="m9 12 2 2 4-4" />
              </svg>
              {t('nav.admin')}
            </Link>
          )}

          {/* Se déconnecter n'a de sens que s'il y a une session à quitter : en mode
              factice l'identité se change par le sélecteur, et déconnecté il n'y a rien
              à déconnecter. */}
          {user && !session.stubAuth && (
            <>
              <div className="mx-1 my-1.5 h-px bg-border" />
              {/* Isolée, et dite comme ce qu'elle est : on ne la déclenche pas en visant la langue. */}
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onLogout();
                }}
                className="flex min-h-[44px] w-full items-center gap-2.5 rounded-lg px-2.5 text-sm text-danger transition-colors hover:bg-danger/10"
              >
                <svg
                  aria-hidden="true"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  className="h-[18px] w-[18px]"
                >
                  <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
                  <line x1="12" y1="2" x2="12" y2="12" />
                </svg>
                {t('session.logout')}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
